import { parseISO } from "date-fns";
import { cn } from "@/lib/utils";
import { formatHours } from "@/lib/format";
import { TONE_PILL, TONE_SURFACE, TONE_TEXT } from "@/lib/widget-status";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import type { TimeGap } from "@/hooks/use-time-gaps";
import { describeGaps, hasGaps, summarizeMissingDays } from "./missing-days";
import { firstGapDate, getDayStatus, groupByIsoWeek, type DayStatus } from "./day-status";

interface Props {
  gaps: TimeGap[];
  /** Månedsnavn i småbokstaver, f.eks. «oktober». */
  monthLabel: string;
  year: number;
  onJump: (date: string) => void;
}

const WEEKDAYS = ["man", "tir", "ons", "tor", "fre"];

const CELL_CLASS: Record<DayStatus, string> = {
  ok: "bg-card border-border hover:bg-muted",
  weekend: "bg-card border-border hover:bg-muted",
  partial: cn(TONE_SURFACE.near, TONE_TEXT.near),
  missing: cn(TONE_SURFACE.miss, TONE_TEXT.miss),
  absence: "bg-muted border-border/50 text-muted-foreground",
  holiday: "bg-muted border-border/50 text-muted-foreground",
  future: "bg-card border-dashed border-border text-muted-foreground",
};

function cellLabel(status: DayStatus, gap: TimeGap): string {
  if (status === "absence") return "Fravær";
  if (status === "holiday") return "Helligdag";
  if (status === "missing") return "0t";
  if (status === "future") return "";
  return formatHours(gap.registeredHours);
}

/**
 * VIB-3034: hullene i måneden øverst på Lukk måned, med en ukekalender
 * (mandag–fredag) der hver dag ruller til raden sin i daglista.
 * Varsler, blokkerer ikke (Erik 28.09).
 */
export default function CloseMonthGapOverview({ gaps, monthLabel, year, onJump }: Props) {
  // useTimeGaps merker framtidige dager som helg, så ukedag avgjøres av datoen.
  const weekdays = gaps.filter((g) => {
    const dow = parseISO(g.date).getDay();
    return dow >= 1 && dow <= 5;
  });
  const summary = summarizeMissingDays(gaps);
  const statuses = weekdays.map(getDayStatus);
  const pastWeekdays = statuses.filter((s) => s !== "future").length;
  if (pastWeekdays === 0) return null;

  if (!hasGaps(summary)) {
    const logged = statuses.filter((s) => s === "ok").length;
    const absent = statuses.filter((s) => s === "absence").length;
    const holidays = statuses.filter((s) => s === "holiday").length;
    const ongoing = statuses.includes("future");
    return (
      <div className="rounded-lg border border-border/50 bg-card p-4 flex items-center gap-3">
        <span className={cn("w-8 h-8 rounded-full grid place-items-center flex-shrink-0", TONE_PILL.reached)}>
          <SrIcon name="check" className="w-4 h-4" />
        </span>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold">
            Alle ukedager {ongoing ? "hittil " : ""}i {monthLabel} er ført
          </h3>
          <p className="text-xs text-muted-foreground">
            {logged} {logged === 1 ? "ukedag" : "ukedager"} ført
            {absent > 0 ? `, ${absent} ${absent === 1 ? "dag" : "dager"} fravær` : ""}
            {holidays > 0 ? `, ${holidays} ${holidays === 1 ? "helligdag" : "helligdager"}` : ""}.
          </p>
        </div>
      </div>
    );
  }

  const first = firstGapDate(gaps);

  return (
    <div className="rounded-lg border border-border/50 bg-card p-4 space-y-4">
      <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <h3 className="text-sm font-semibold">
            Hull i {monthLabel} {year}
          </h3>
          <p className="text-sm text-foreground">
            <span className="font-medium">Du har {describeGaps(summary)}.</span>{" "}
            <span className="text-muted-foreground">Helger, helligdager, fravær og dager fram i tid er ikke regnet med.</span>
          </p>
        </div>
        {first && (
          <Button variant="outline" size="sm" className="w-full xl:w-auto flex-shrink-0" onClick={() => onJump(first)}>
            Gå til første tomme dag
            <SrIcon name="arrow-down" className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>

      <div className="grid grid-cols-[28px_repeat(5,minmax(0,1fr))] xl:grid-cols-[52px_repeat(5,minmax(0,1fr))] gap-1.5">
        <span />
        {WEEKDAYS.map((d) => (
          <span key={d} className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground text-center">
            {d}
          </span>
        ))}
        {groupByIsoWeek(weekdays).map(({ week, items }) => {
          const byDow = new Map(items.map((g) => [parseISO(g.date).getDay(), g]));
          return [
            <span key={`w-${week}`} className="text-[11px] text-muted-foreground tabular-nums self-center">
              <span className="hidden xl:inline">Uke </span>
              {week}
            </span>,
            ...[1, 2, 3, 4, 5].map((dow) => {
              const gap = byDow.get(dow);
              if (!gap) return <span key={`${week}-${dow}`} />;
              const status = getDayStatus(gap);
              const label = cellLabel(status, gap);
              const day = parseISO(gap.date).getDate();
              return (
                <button
                  key={gap.date}
                  type="button"
                  disabled={status === "future"}
                  onClick={() => onJump(gap.date)}
                  aria-label={`${WEEKDAYS[dow - 1]} ${day}. ${monthLabel}${label ? `, ${label}` : ""}`}
                  className={cn(
                    "rounded-md border px-2 py-1.5 flex flex-col items-start gap-0.5 text-[13px] font-medium tabular-nums transition-colors disabled:cursor-default",
                    CELL_CLASS[status]
                  )}
                >
                  {day}
                  {label && <span className="hidden xl:block text-[10.5px] font-normal opacity-80">{label}</span>}
                </button>
              );
            }),
          ];
        })}
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm border border-border bg-card" />
          Ført
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className={cn("w-3 h-3 rounded-sm border", TONE_SURFACE.near)} />
          Færre timer enn forventet
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className={cn("w-3 h-3 rounded-sm border", TONE_SURFACE.miss)} />
          Ingen timer
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm border border-border/50 bg-muted" />
          Fravær eller helligdag
        </span>
      </div>
    </div>
  );
}
